'use client';

import dynamic from 'next/dynamic';
import { type ChangeEvent, useEffect, useMemo, useRef } from 'react';
import { Button } from '@/app/components/button';
import { LocationSelector } from '@/app/components/form-kit/fields/location-selector';
import { Checkbox } from '@/app/components/forms/checkbox';
import { FieldGroup } from '@/app/components/forms/field-group';
import { Radio } from '@/app/components/forms/radio';
import { Select } from '@/app/components/forms/select';
import { TextField } from '@/app/components/forms/text-field';
import { Container } from '@/app/components/layouts/container';
import { Link } from '@/app/components/navigation/link';
import { Tooltip } from '@/app/components/overlays/tooltip';
import { FORM_MESSAGES } from '@/app/constants/forms';
import { useDisclosure } from '@/app/hooks/useDisclosure';
import { type EligibilityCitizenType, MAXIMUM_PASSPORT_LENGTH } from '@/app/lcv/components/campaigns/big-campaign-2026/actions/schema';
import { KV_IMAGE, RegisterStep } from '@/app/lcv/components/campaigns/big-campaign-2026/constants';
import { KvBanner } from '@/app/lcv/components/campaigns/big-campaign-2026/shared/kv-banner';
import { traceStep } from '@/app/lcv/components/campaigns/big-campaign-2026/shared/trace-client';
import { createModelOptionGroups } from '@/app/lcv/components/campaigns/big-campaign-2026/shared/utils';
import { createBigCampaign2026Schema } from '@/app/lcv/components/campaigns/big-campaign-2026/step/form/schema';
import { desktopMaxWidthFormClassName } from '@/app/lcv/components/campaigns/big-campaign-2026/styles';
import { BIG_CAMPAIGN_2026_EVENT, STEP_NAME, trackBigCampaign2026 } from '@/app/lcv/components/campaigns/big-campaign-2026/tracking';
import type { SavedFormInputType, StepFormProps } from '@/app/lcv/components/campaigns/big-campaign-2026/types';
import { useForm } from '@/app/lib/forms/hooks/useForm';
import { createFullnameFieldValidator, transformToOnlyNumber } from '@/app/lib/forms/utils/validations.function';
import { cn } from '@/app/utils/cn';
import { createDataAttributes } from '@/app/utils/createDataAttributes';
import { createDataGA } from '@/app/utils/createDataGA';
import { REGEX_PASSPORT } from '@/app/utils/regex';
const ModalCondition = dynamic(() => import('@/app/lcv/components/campaigns/big-campaign-2026/step/form/modal-condition').then(mod => mod.ModalCondition), {
  ssr: false
});
const SpecialOffersCondition = dynamic(() => import('@/app/lcv/components/campaigns/big-campaign-2026/shared/special-offers/condition').then(mod => mod.SpecialOffersCondition), {
  ssr: false,
  loading: () => <div className="mt-8 flex h-14 flex-col items-center space-y-4 xl:hidden" />
});
const SpecialOffersCTA = dynamic(() => import('@/app/lcv/components/campaigns/big-campaign-2026/shared/special-offers/cta').then(mod => mod.SpecialOffersCTA), {
  ssr: false
});
const MAX_CITIZEN_NUM = 13;
const CITIZEN_OPTIONS: {
  label: string;
  value: EligibilityCitizenType;
}[] = [{
  label: 'เลขบัตรประชาชน',
  value: 'national_id'
}, {
  label: 'เลขหนังสือเดินทาง',
  value: 'passport'
}] as const;
const FORM_SUBMIT_ID = 'register-form';
export const StepForm = ({
  variant,
  imageCover = KV_IMAGE,
  savedForm,
  preSelectedDealer,
  preSelectedModelCode,
  provinces: defaultProvinces,
  models,
  isLoading = false,
  clearForm = false,
  offerDetail,
  formName,
  tracking,
  onSubmit
}: StepFormProps) => {
  const citizenInputRef = useRef<HTMLInputElement | null>(null);
  const citizenTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [showTooltip, {
    open: openTooltip,
    close: closeTooltip
  }] = useDisclosure(false);
  const [isOpenCondition, {
    open: openCondition,
    close: closeCondition
  }] = useDisclosure(false);
  const modelOptions = useMemo(() => createModelOptionGroups(models), [models]);
  const hasModelOptions = modelOptions.length > 0;
  const isSubmitDisabled = isLoading || !hasModelOptions;

  // `?modelCode=` prefill, if it is a campaign model.
  const validPreSelectedModelCode = models.some(model => model.modelCode === preSelectedModelCode) ? preSelectedModelCode : undefined;
  const initialModelCode = savedForm?.modelCode ?? validPreSelectedModelCode ?? '';
  const {
    values,
    formState,
    setValue,
    handleSubmit,
    registerRef,
    reset
  } = useForm({
    schema: createBigCampaign2026Schema(),
    tracking,
    initialValues: {
      fullname: savedForm?.fullname ?? '',
      phoneNumber: savedForm?.phoneNumber ?? '',
      type: 'national_id',
      citizenId: savedForm?.citizenId ?? '',
      modelCode: initialModelCode ?? '',
      province: preSelectedDealer?.province ?? '',
      district: preSelectedDealer?.district ?? '',
      dealer: preSelectedDealer?.branchCode ?? '',
      privacy: false
    },
    validators: {
      fullname: createFullnameFieldValidator(() => formState),
      phoneNumber: {
        transform: (value: string) => transformToOnlyNumber(value)
      },
      citizenId: {
        transform: (value: string) => {
          const isNationalId = values.type === 'national_id';
          if (isNationalId) {
            return transformToOnlyNumber(value);
          }
          return value.toUpperCase().replace(REGEX_PASSPORT, '');
        }
      }
    }
  });
  const dealerData = useMemo(() => {
    if (!preSelectedDealer || !preSelectedDealer?.name_th) return undefined;
    const [name, branch] = preSelectedDealer.name_th.split('(สาขา');
    return {
      name,
      branch: branch ? `(สาขา${branch}` : ''
    };
  }, [preSelectedDealer]);
  const handleOnChageCitizenType = (val: EligibilityCitizenType) => {
    setValue('type', val);
    setValue('citizenId', '');
    if (citizenTimeoutRef.current) {
      clearTimeout(citizenTimeoutRef.current);
    }
    const inputEl = citizenInputRef.current?.children[0] as HTMLInputElement;
    inputEl?.blur();
    citizenTimeoutRef.current = setTimeout(() => {
      inputEl?.focus();
    }, 100);
  };
  const handleOnChangeConsent = (event: ChangeEvent<HTMLInputElement>) => {
    if (showTooltip) {
      closeTooltip();
    }
    setValue('privacy', event.target.checked);
  };
  const triggerPrivacyWarning = () => {
    if (showTooltip) return;
    openTooltip();
  };
  const onFormSubmit = (event?: React.FormEvent) => {
    if (isSubmitDisabled) return;
    if (!values.privacy) {
      triggerPrivacyWarning();
    }
    handleSubmit(async (data: SavedFormInputType) => onSubmit(data))(event);
  };
  useEffect(() => {
    if (!!preSelectedDealer) {
      trackBigCampaign2026({
        step: RegisterStep.form,
        groupName: formName,
        text: 'Form init with dealer',
        value: 'form_init_dealer'
      });
    } else {
      trackBigCampaign2026({
        step: RegisterStep.form,
        groupName: formName,
        text: 'Form init normal',
        value: 'form_init_normal'
      });
    }
    return () => {
      if (citizenTimeoutRef.current) {
        clearTimeout(citizenTimeoutRef.current);
      }
    };
  }, [formName, preSelectedDealer]);

  // Trace 05: the model the form starts with (URL prefill vs campaign list).
  useEffect(() => {
    traceStep('formPrefill', {
      preSelectedModelCode: preSelectedModelCode ?? '',
      validPreSelectedModelCode: validPreSelectedModelCode ?? '',
      initialModelCode,
      modelsCount: models.length
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preSelectedModelCode, validPreSelectedModelCode]);

  // URL params can arrive after the first render: prefill then, untracked.
  useEffect(() => {
    if (!validPreSelectedModelCode || values.modelCode) return;
    setValue('modelCode', validPreSelectedModelCode, {
      track: false
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validPreSelectedModelCode]);
  useEffect(() => {
    if (!preSelectedDealer || values.dealer) return;
    setValue('province', preSelectedDealer.province ?? '', {
      track: false
    });
    setValue('district', preSelectedDealer.district ?? '', {
      track: false
    });
    setValue('dealer', preSelectedDealer.branchCode ?? '', {
      track: false
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preSelectedDealer]);
  useEffect(() => {
    if (!clearForm) return;
    reset();
    setValue('type', 'national_id');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clearForm]);
  return <>
      <KvBanner imageCover={imageCover} />
      <div className={cn('relative w-full xl:mb-0', {
      'bg-silver-50 top-5 -mt-8 -mb-4 rounded-t-lg md:-top-10 md:mt-0 md:-mb-10 xl:top-10': variant === 'default',
      'bg-white': variant === 'offer'
    })} {...createDataAttributes({
      test: 'step-form'
    })}>
        <Container className={cn('pt-4 pb-5 md:pb-0 xl:px-0 xl:pt-0 xl:pb-6', desktopMaxWidthFormClassName)}>
          {variant === 'offer' && (offerDetail?.detail ? <SpecialOffersCondition wrapperClassName="py-4 xl:hidden" initialOpen={false} detail={offerDetail?.detail || ''} /> : <div className="py-4 xl:hidden" />)}
          <h2 className={cn('text-sub-h2 font-noto-bold where-span:text-inline-wrap mb-4 text-gray-700 xl:block xl:text-gray-900', {
          'lg:mt-8': variant === 'offer'
        })} {...createDataAttributes({
          test: 'title'
        })}>
            ลงทะเบียนรับข้อเสนอพิเศษ<span>และทดลองขับ</span>
          </h2>

          {!!dealerData && <div className="mb-4 rounded-sm bg-gray-50 p-3" {...createDataAttributes({
          test: 'pre-selected-dealer'
        })}>
              <p className="font-noto-bold text-body-1-fixed mb-1" {...createDataAttributes({
            test: 'dealer-name'
          })}>
                {dealerData.name}
              </p>
              {!!dealerData.branch && <p className="text-body-2-fixed" {...createDataAttributes({
            test: 'dealer-branch'
          })}>
                  {dealerData.branch}
                </p>}
            </div>}
          <form ref={tracking?.observeView} id={FORM_SUBMIT_ID} className="safe-area-scrollto relative space-y-4 xl:pt-0" noValidate {...createDataAttributes({
          test: 'register-form'
        })} onSubmit={onFormSubmit}>
            <div className="safe-area-scrollto grid gap-4 xl:grid-cols-2">
              <TextField ref={registerRef('fullname')} label="ชื่อ - นามสกุล" name="fullname" placeholder="กรอกชื่อ - นามสกุล" required maxLength={50} disabled={isLoading} value={values.fullname} error={!!formState.errors.fullname} errorMessage={formState.errors.fullname} onChange={e => setValue('fullname', e?.target?.value || '')} groupAttributes={createDataAttributes({
              error: formState.errors.fullname || undefined
            })} inputClassName="webkit-autofill-text-gray-900" />
              <TextField ref={registerRef('phoneNumber')} id="input-mobile" type="tel" name="input-mobile" label="เบอร์โทรศัพท์" placeholder="กรอกเบอร์ของคุณ" maxLength={10} value={values.phoneNumber} error={!!formState.errors.phoneNumber} errorMessage={formState.errors.phoneNumber} disabled={isLoading} inputClassName="webkit-autofill-text-gray-900" required groupAttributes={createDataAttributes({
              error: formState.errors.phoneNumber || undefined
            })} onChange={e => setValue('phoneNumber', e?.target?.value || '')} />
            </div>
            <FieldGroup htmlFor="citizenId" inputType="control" label="เลขบัตรประชาชน / เลขหนังสือเดินทาง" error={!!formState.errors.citizenId} errorMessage={formState.errors.citizenId} required {...createDataAttributes({
            error: !!formState.errors.citizenId || undefined
          })}>
              <Radio name="type" options={CITIZEN_OPTIONS} required value={values.type} onChange={handleOnChageCitizenType} disabled={isLoading} tracking={{
              eventName: BIG_CAMPAIGN_2026_EVENT,
              group: STEP_NAME[RegisterStep.form]
            }} />

              <TextField ref={el => {
              citizenInputRef.current = el;
              registerRef('citizenId')(el);
            }} name="citizenId" type={values.type === 'national_id' ? 'tel' : 'text'} placeholder={values.type === 'national_id' ? `กรอกเลขบัตรประชาชน ${MAX_CITIZEN_NUM} หลัก` : 'กรอกเลขหนังสือเดินทาง'} value={values.citizenId} error={!!formState.errors.citizenId} maxLength={values.type === 'national_id' ? MAX_CITIZEN_NUM : MAXIMUM_PASSPORT_LENGTH} inputClassName="webkit-autofill-text-gray-900" required disabled={isLoading} onChange={e => setValue('citizenId', e?.target?.value || '')} />
            </FieldGroup>

            <Select ref={registerRef('modelCode')} name="modelCode" label="เลือกรุ่นรถที่สนใจ" placeholder="เลือกรุ่นรถ" required options={modelOptions} value={values.modelCode} error={!!formState.errors.modelCode} errorMessage={formState.errors.modelCode} disabled={isLoading || !hasModelOptions} onChange={e => setValue('modelCode', e?.target?.value || '')} {...createDataAttributes({
            error: formState.errors.modelCode || undefined
          })} />

            {!dealerData && <>
                <p className="text-body-1-fixed font-noto-bold text-gray-700" {...createDataAttributes({
              test: 'select-dealer-title'
            })}>
                  ผู้จำหน่ายที่สนใจ
                </p>
                <div className="grid gap-4 *:scroll-mt-16 lg:grid-cols-2" {...createDataAttributes({
              test: 'location-selector-group'
            })}>
                  <LocationSelector formLayout="two-column" provinces={defaultProvinces} errors={formState.errors} values={values} setValue={setValue} registerRef={registerRef} required disabled={isLoading} dealerColSpan={2} {...createDataAttributes({
                test: 'select-dealer-group'
              })} />
                </div>
              </>}

            <div
          // So a missing consent scrolls here on submit.
          ref={registerRef('privacy')} className="flex pt-2 pb-5" {...createDataAttributes({
            test: 'terms-consent'
          })}>
              <Tooltip theme="dark" text="กรุณากดยอมรับ <span>นโยบายความเป็นส่วนตัว</span>" placement="top-left" show={showTooltip} arrowCenter={false} tooltipClassName="w-full whitespace-nowrap" arrowClassName="-left-1" manualShow onClose={closeTooltip}>
                <Checkbox name="isConsent" error={!!formState.errors.privacy} disabled={isLoading} onChangeInput={handleOnChangeConsent} options={[{
                name: 'isConsent',
                checked: !!values.privacy,
                children: <p className="where-span:text-inline-wrap">
                          อ่านและยอมรับ{' '}
                          <Link theme="light" href="/privacy-policy" target="_blank" {...createDataAttributes({
                    test: 'privacy-link'
                  })} {...createDataGA({
                    click: true,
                    eventName: BIG_CAMPAIGN_2026_EVENT,
                    group: STEP_NAME[RegisterStep.form],
                    text: 'นโยบายความเป็นส่วนตัว',
                    value: 'open_privacy'
                  })}>
                            นโยบายความเป็นส่วนตัว
                          </Link>{' '}
                          และ{' '}
                          <button type="button" className="link-light cursor-pointer" {...createDataAttributes({
                    test: 'tandc-link'
                  })} {...createDataGA({
                    click: true,
                    eventName: BIG_CAMPAIGN_2026_EVENT,
                    group: STEP_NAME[RegisterStep.form],
                    text: 'เงื่อนไขและข้อกำหนด',
                    value: 'open_term_condition'
                  })} onClick={openCondition}>
                            เงื่อนไขและข้อกำหนด
                          </button>{' '}
                          ของกิจกรรม
                        </p>
              }]} />
              </Tooltip>
            </div>
          </form>
        </Container>
      </div>

      {/* Full-width bar background. */}
      <div className={cn('w-full xl:relative xl:mb-0', {
      'bg-silver-50 sticky bottom-0 xl:mt-6': variant === 'default',
      'bg-white': variant === 'offer'
    })}>
        <Container className={cn('py-4 xl:pt-0 xl:pb-8', desktopMaxWidthFormClassName)}>
          <Button form={FORM_SUBMIT_ID} className="w-full xl:w-auto" type="submit" variant="primary" disabled={isSubmitDisabled} {...createDataAttributes({
          test: 'submit-button'
        })} {...createDataGA({
          click: true,
          eventName: BIG_CAMPAIGN_2026_EVENT,
          group: STEP_NAME[RegisterStep.form],
          text: 'ยืนยัน',
          value: 'register_submit_click'
        })}>
            {isLoading ? FORM_MESSAGES.submitting : FORM_MESSAGES.submit}
          </Button>

          {variant === 'offer' && <div className="mt-8 mb-4 flex flex-col items-center space-y-4 xl:hidden">
              <SpecialOffersCTA />
            </div>}
        </Container>
      </div>

      {!!isOpenCondition && <ModalCondition onClose={closeCondition} />}
    </>;
};